import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import CandidateBasicInformation from './CandidateBasicInformation';
import CandidateAddressDetails from './CandidateAddressDetails';
import { getPersonalInfoCustomFields } from '../../../Admin/PreboardingConfiguration/preboardingConfigurationApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomField, SectionProps } from '../../../Admin/PreboardingConfiguration/CandidateDetailsConfiguration/CandidateDetailsConfiguration';
import CandidateEmergencyContact from './CandidateEmergencyContact';
import CandidateBankDetails from './CandidateBankDetails';
import CandidateGraduationDetails from './CandidateGraduationDetails';
import CandidateSchoolingDetails from './CandidateSchoolingDetails';
import CandidateGovernmentIds from './CandidateGovernmentIds';
import CandidateEmployementDetails, { EmploymentRow } from './CandidateEmployementDetails';
import CandidateSkillsDetails from './CandidateSkillsDetails';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { useCandidatePreboarding } from '../CandidatePreboardingContext';
import { useGlobalContext } from '../../../../../Context/GlobalContext';
import { addOrUpdateCandidateDetails, getCandidateCustomFieldValues } from '../candidatePreboardingApi';
import { theme } from '../../../../../theme';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import { validateEmail } from '../../../Validation/validation';
import { useLoadingBackdrop } from '../../../../../Context/LoadingBackdropContext';

const CandidatePersonalDetails: React.FC = () => {
  const isCandidate = !!sessionStorage.getItem("candidate_session");
  const { tenant } = useParams<{ tenant: string }>();
  const [sectionNames, setSectionNames] = useState<SectionProps[]>([]);
  const [retrievedFields, setRetrievedFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fieldValues, setFieldValues] = useState<{ [key: number]: any }>({});
  const [fieldErrors, setFieldErrors] = useState<{ [key: number]: string }>({});
  const [isFullTimeExperience, setIsFullTimeExperience] = useState<boolean>(false);
  const { showSnackbar } = useSnackbar();
  const { show, hide } = useLoadingBackdrop();
  const employmentRef = useRef<HTMLDivElement | null>(null);
  const [employmentHistory, setEmploymentHistory] = useState<EmploymentRow[]>([]);
  const { candidateData, fetchCandidateData } = useCandidatePreboarding();
  const { userData } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchCustomFields = async () => {
    try {
      setLoading(true);
      const result = await getPersonalInfoCustomFields(tenant, isCandidate);
      if (result) {
        const transformedFields = result.Data.map((field: any) => {
          const isDropdown = field.FieldType === "dropdown";
          return {
            customFieldId: field.CustomFieldId,
            name: field.FieldName,
            type: field.FieldType,
            mandatory: field.IsMandatory,
            sectionName: field.SectionName,
            sectionId: field.SectionId,
            options: isDropdown ? field.Options || [] : undefined,
            optionsRaw: isDropdown && field.Options ? field.Options.join(", ") : undefined,
            isDefault: field.IsDefault || false,
            placeholder: field.Placeholder || '',
          };
        });

        setRetrievedFields(transformedFields);
        setSectionNames(result.SectionNames.map((section: any) => ({
          sectionName: section.SectionName,
          sectionId: section.SectionId
        })));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomFields();
  }, [tenant]);


  // Validate all mandatory fields
  const validateFields = () => {
    const errors: { [key: string]: string } = {};

    retrievedFields.forEach((field) => {
      const isEmploymentField =
        field.sectionName.toLowerCase() === "employment details";

      // ❌ Skip validation for employment details if user selected "No"
      if (isEmploymentField && !isFullTimeExperience) {
        return;
      }

      const value = fieldValues[field.customFieldId];

      // ✅ Mandatory check
      if (field.mandatory && !value) {
        errors[field.customFieldId] = `${field.name} is required.`;
      }

      // ✅ Email validation for "Personal Email Address"
      if (
        field.name.toLowerCase() === "personal email address" &&
        value &&
        !validateEmail(value)
      ) {
        errors[field.customFieldId] = "Please enter a valid email address.";
      }

      // ✅ Date validation for Start Date and End Date
      if (
        isEmploymentField &&
        (field.name.toLowerCase() === "start date" ||
          field.name.toLowerCase() === "end date")
      ) {
        const startField = retrievedFields.find(
          (f) =>
            f.sectionName.toLowerCase() === "employment details" &&
            f.name.toLowerCase() === "start date"
        );
        const endField = retrievedFields.find(
          (f) =>
            f.sectionName.toLowerCase() === "employment details" &&
            f.name.toLowerCase() === "end date"
        );

        if (startField && endField) {
          const startValue = fieldValues[startField.customFieldId];
          const endValue = fieldValues[endField.customFieldId];
          if (startValue && endValue) {
            const startDate = new Date(startValue);
            const endDate = new Date(endValue);
            if (startDate > endDate) {
              errors[startField.customFieldId] =
                "Start Date cannot be after End Date.";
              errors[endField.customFieldId] =
                "End Date cannot be before Start Date.";
            }
          }
        }
      }
    });

    // Employment details: startDate and endDate validation
    if (isFullTimeExperience && employmentHistory.length > 0) {
      employmentHistory.forEach((entry, idx) => {
        if (!entry.employer) {
          errors[`employment_employer_${idx}`] = `Employer is required.`;
        }
        if (!entry.designation) {
          errors[`employment_designation_${idx}`] = `Designation is required.`;
        }
        if (!entry.startDate) {
          errors[`employment_startDate_${idx}`] = `Start Date is required.`;
        }
        if (!entry.endDate) {
          errors[`employment_endDate_${idx}`] = `End Date is required.`;
        }
        if (entry.startDate && entry.endDate) {
          const start = new Date(entry.startDate);
          const end = new Date(entry.endDate);
          if (start > end) {
            errors[`employment_dateOrder_${idx}`] =
              "Start Date cannot be after End Date for employment entry.";
          }
        }
      });
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      showSnackbar("Please fill all mandatory fields", "error");
      return false;
    }
    return true;
  };


  const transformFormValues = (fieldValues: { [key: number]: any }) => {
    return Object.entries(fieldValues).map(([fieldId, value]) => ({
      FieldId: Number(fieldId), // convert string → number
      FieldValue: value
    }));
  };



  // Handle field value change
  const handleFieldChange = (fieldId: number, value: any) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[fieldId]) {
        delete newErrors[fieldId];
      }
      return newErrors;
    });
  };

  // Example submit handler (can be used in parent or child)
  const handleSubmit = async () => {
    if (!validateFields()) return

    const payLoad = {
      MapID: candidateData?.mapID,
      CustomFieldValues: transformFormValues(fieldValues),
      EmploymentHistory: isFullTimeExperience ? employmentHistory.map(entry => ({
        EmploymentId: entry.employmentId || 0, // Use existing ID or 0 for new entries
        OrganizationName: entry.employer,
        Designation: entry.designation,
        StartDate: entry.startDate,
        EndDate: entry.endDate
      })) : [],
      UserNum: userData.UserNum || null,
      PreboardingStatusKey: candidateData?.preboardingStatusKey,
      RecruiterName: candidateData?.recruiterName || '',
      RecruiterEmail: candidateData?.recruiterEmail || '',
      IsCandidate: isCandidate
    };
    try {
      show()
      const response = await addOrUpdateCandidateDetails(tenant, payLoad, isCandidate);

      if (response) {
        if (response.Status) {
          await fetchCandidateData();
          navigate(`/${tenant}/preboarding/all-candidates/candidate-preboarding/documents`, {
            state: location.state
          });
        } else {
          showSnackbar(response.Message || "Error saving details", "error");
        }
      }
    } catch (error) {
      showSnackbar("Error saving details", "error");
      console.log(error)
    } finally {
      hide()
    }
  };


  const hasFullTimeExperience = retrievedFields.find(field => field.sectionName.toLowerCase() === 'employment details' && field.name.toLowerCase() === 'has full time experience');

  const handleFullTimeExperienceChange = (id: number, value: string) => {
    setIsFullTimeExperience(value === "yes"); // maps yes→true, no→false
    handleFieldChange(id, value === "yes" ? true : false); // also update in fieldValues for validation
    // Scroll only when user selects "Yes"
    if (value === "yes" && employmentRef.current) {
      setTimeout(() => {
        employmentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50); // slight delay to ensure DOM updated
    }
  };

  const fetchCandidateCustomFieldValues = async () => {
    try {
      const data = await getCandidateCustomFieldValues(tenant, { "MapID": candidateData?.mapID }, isCandidate);
      if (data) {
        const fieldMapData = data.CustomFieldValues.map((item: { FieldId: number; FieldValue: any }) => ({ [item.FieldId]: item.FieldValue }));
        const combinedFieldValues = Object.assign({}, ...fieldMapData);
        setFieldValues(combinedFieldValues);
        const employmentData = data.EmploymentHistory.map((item: any, idx: any) => ({
          id: idx + 1, // unique sequential id for frontend management
          employer: item.OrganizationName,
          designation: item.Designation,
          startDate: item.StartDate,
          endDate: item.EndDate,
          employmentId: item.EmploymentId // backend EmploymentId
        }));
        setEmploymentHistory(employmentData);
        const hasFullTimeExperience = data.CustomFieldValues.find((item: { FieldId: number; FieldValue: any, SectionName: string, FieldName: string }) => {
          return item.FieldName.toLowerCase() === "has full time experience" && item.SectionName.toLowerCase() === "employment details";
        })?.FieldValue;
        setIsFullTimeExperience(hasFullTimeExperience === "True");
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (candidateData?.mapID) {
      fetchCandidateCustomFieldValues();
    }
  }, [tenant, candidateData?.mapID]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {
        candidateData?.isOfferAccepted === false && (
          <Box sx={{ bgcolor: theme.palette.primary.light, width: '100%', mt: '1vw', display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
            <WarningOutlinedIcon sx={{ color: theme.palette.warning.main }} />
            <Typography variant='subtitle1'>
              Action Restricted : This action will be enabled only after the candidate approves the offer letter.
            </Typography>
          </Box>
        )
      }


      <Stack direction="column" spacing={2} mt={2}>
        <Typography variant="h5">Personal Details</Typography>
        <CandidateBasicInformation
          retrievedFields={retrievedFields}
          sectionData={sectionNames[0]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />
        <CandidateAddressDetails
          retrievedFields={retrievedFields}
          sectionData={sectionNames[1]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />
        <CandidateEmergencyContact
          retrievedFields={retrievedFields}
          sectionData={sectionNames[2]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />

        <CandidateSkillsDetails
          retrievedFields={retrievedFields}
          sectionData={sectionNames.find(section => section.sectionName.toLowerCase() === 'skills')}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />

        <Typography variant="h5">Professional Details</Typography>
        <CandidateBankDetails
          retrievedFields={retrievedFields}
          sectionData={sectionNames[3]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />
        <CandidateGovernmentIds
          retrievedFields={retrievedFields}
          sectionData={sectionNames[4]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />
        <CandidateSchoolingDetails
          retrievedFields={retrievedFields}
          sectionData={sectionNames[5]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />
        <CandidateGraduationDetails
          retrievedFields={retrievedFields}
          sectionData={sectionNames[6]}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          onFieldChange={handleFieldChange}
        />
        {/* <Stack direction="row" spacing={2}>
          <Box>
            <Typography variant="subtitle2">
              Do you have prior full-time work experience?
            </Typography>
            <Typography variant="caption">
              (Freshers can select “No”)
            </Typography>
          </Box>
        </Stack> */}

        {
          hasFullTimeExperience && (
            <Box
              sx={{
                display: "flex",
                gap: 10,
                alignItems: "center",   // 👈 align items from the top
                justifyContent: "flex-start"
              }}
            >
              <Stack direction="column">
                <Typography variant="body1">
                  Do you have prior full-time work experience?
                </Typography>
              </Stack>

              <FormControl component="fieldset" required={hasFullTimeExperience.mandatory} error={!!fieldErrors[hasFullTimeExperience.customFieldId]}>
                <RadioGroup
                  value={
                    isFullTimeExperience
                      ? "yes"
                      : "no"
                  }
                  onChange={(e) => handleFullTimeExperienceChange(hasFullTimeExperience.customFieldId, e.target.value)}
                  sx={{ display: "flex", flexDirection: "row", gap: 5, alignItems: "flex-start" }} // 👈 also ensure radios align top
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label={
                      <Typography variant="subtitle3">
                        <strong>Yes</strong> - I have worked in a full-time role before.
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label={
                      <Typography variant="subtitle3">
                        <strong>No</strong> - I am a fresher without prior work experience.
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

          )
        }

        {isFullTimeExperience && hasFullTimeExperience && (
          <Box ref={employmentRef}>
            <CandidateEmployementDetails
              retrievedFields={retrievedFields}
              sectionData={sectionNames[7]}
              fieldValues={fieldValues}
              fieldErrors={fieldErrors}
              onFieldChange={handleFieldChange}
              employmentHistory={employmentHistory}
              setEmploymentHistory={setEmploymentHistory}
              setFieldErrors={setFieldErrors}
            />
          </Box>
        )}

        <Divider sx={{ mb: 2, mt: 1 }} />

        {
          (candidateData?.preboardingStatusKey === "PendingDocumentVerification" || candidateData?.preboardingStatusKey === "BGVPending" || candidateData?.preboardingStatusKey === "Hired" || candidateData?.preboardingStatusKey === "BGVRejected") ? (
            <></>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2,
                mb: 4
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={candidateData?.isOfferAccepted === false}
              >
                Submit
              </Button>

            </Box>
          )
        }




      </Stack>
    </Box>
  );
};
export default CandidatePersonalDetails;
